import React, { useState, useEffect, memo } from 'react';
import { API_ENDPOINTS } from '../../../utils/apiConfig';
import { useTracking } from '../../../hooks/useTracking';

/**
 * Widget pour envoyer manuellement les rappels de prestation J-1
 * Accessible aux admins et planificateurs
 */
const SendRemindersWidget = memo(({ token }) => {
  const { trackError } = useTracking();
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Charger le statut et la prévisualisation au montage
  useEffect(() => {
    loadStatus();
    loadPreview();
  }, [token]);

  const loadStatus = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/notifications/reminders/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data.status);
      }
    } catch (err) {
      console.error('Erreur chargement statut:', err);
      trackError('reminders_status_load_error', { error: err.message, component: 'SendRemindersWidget' });
    }
  };

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/notifications/reminders/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      } else {
        setError('Erreur lors du chargement de la prévisualisation');
        trackError('reminders_preview_load_error', { status: response.status, component: 'SendRemindersWidget' });
      }
    } catch (err) {
      setError('Erreur réseau');
      trackError('reminders_preview_network_error', { error: err.message, component: 'SendRemindersWidget' });
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async () => {
    if (!window.confirm(`Envoyer ${preview?.wouldReceive || 0} email(s) de rappel ?`)) {
      return;
    }

    setSending(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/notifications/reminders/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        loadPreview(); // Recharger la prévisualisation
      } else {
        setError(data.error || 'Erreur lors de l\'envoi');
        trackError('reminders_send_error', { error: data.error, component: 'SendRemindersWidget' });
      }
    } catch (err) {
      setError('Erreur réseau');
      trackError('reminders_send_network_error', { error: err.message, component: 'SendRemindersWidget' });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <span style={styles.icon}>📧</span>
        <h3 style={styles.title}>Rappels J-1</h3>
      </div>

      {/* Statut du scheduler */}
      {status && (
        <div style={styles.statusBar}>
          <span style={{
            ...styles.statusDot,
            backgroundColor: status.isEnabled ? '#4CAF50' : '#f44336'
          }} />
          <span style={styles.statusText}>
            {status.isEnabled ? 'Actif' : 'Désactivé'} - {status.scheduledTime}
          </span>
        </div>
      )}

      {/* Prévisualisation */}
      {loading ? (
        <p style={styles.loading}>Chargement...</p>
      ) : preview ? (
        <div style={styles.previewBox}>
          <p style={styles.previewTitle}>
            Prestations du {formatDate(preview.targetDate)}
          </p>
          <div style={styles.statsRow}>
            <div style={styles.stat}>
              <span style={styles.statValue}>{preview.total || 0}</span>
              <span style={styles.statLabel}>Participants</span>
            </div>
            <div style={styles.stat}>
              <span style={{ ...styles.statValue, color: '#4CAF50' }}>{preview.wouldReceive || 0}</span>
              <span style={styles.statLabel}>Recevraient</span>
            </div>
            <div style={styles.stat}>
              <span style={{ ...styles.statValue, color: '#ff9800' }}>{preview.wouldSkip || 0}</span>
              <span style={styles.statLabel}>Ignorés</span>
            </div>
          </div>
          {/* Détail par type */}
          {preview.wouldReceive > 0 && (
            <div style={styles.detailRow}>
              <span style={styles.detailItem}>
                🚛 {preview.fromDepotCount || 0} depuis dépôt
              </span>
              <span style={styles.detailItem}>
                📍 {preview.onSiteCount || 0} sur place
              </span>
            </div>
          )}
        </div>
      ) : null}

      {/* Message si pas de prestations */}
      {preview && preview.total === 0 && (
        <p style={styles.noData}>Aucune prestation prévue</p>
      )}

      {/* Erreur */}
      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {/* Résultat */}
      {result && (
        <div style={styles.result}>
          {result.sent} envoyé(s), {result.failed} échec(s), {result.skipped} ignoré(s)
        </div>
      )}

      {/* Bouton d'envoi */}
      <button
        onClick={sendReminders}
        disabled={sending || !preview || preview.wouldReceive === 0}
        style={{
          ...styles.button,
          opacity: (sending || !preview || preview.wouldReceive === 0) ? 0.6 : 1
        }}
      >
        {sending ? 'Envoi en cours...' : 'Envoyer les rappels'}
      </button>
    </div>
  );
});

SendRemindersWidget.displayName = 'SendRemindersWidget';

const styles = {
  widget: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '15px'
  },
  icon: {
    fontSize: '24px'
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '15px',
    padding: '8px 12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px'
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%'
  },
  statusText: {
    fontSize: '13px',
    color: '#666'
  },
  loading: {
    color: '#666',
    textAlign: 'center',
    padding: '20px'
  },
  previewBox: {
    backgroundColor: '#e3f2fd',
    borderRadius: '6px',
    padding: '15px',
    marginBottom: '15px'
  },
  previewTitle: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    fontWeight: '500',
    color: '#1976d2'
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-around'
  },
  stat: {
    textAlign: 'center'
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(25, 118, 210, 0.2)'
  },
  detailItem: {
    fontSize: '13px',
    color: '#666'
  },
  statValue: {
    display: 'block',
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333'
  },
  statLabel: {
    fontSize: '12px',
    color: '#666'
  },
  noData: {
    color: '#999',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: '15px'
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '15px',
    fontSize: '13px'
  },
  result: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '15px',
    fontSize: '13px'
  },
  button: {
    marginTop: 'auto',
    padding: '12px 20px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  }
};

export default SendRemindersWidget;
